import { useCallback, useEffect, useMemo, useState } from "react";
import BaseWrapper from "../../components/ui/BaseWrapper";
import { useMutation, useQuery } from "@tanstack/react-query";

import dayjs from "dayjs";
import WorkOrderStatus from "../../components/WorkOrderStatus";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FilterChip from "../../components/FilterChip";
import {
  clearAuthStorage,
  debounce,
  formatPhoneNumber,
  navigateWithReload,
} from "../../utils/helpers";
import BaseLoader from "../../components/ui/BaseLoader";
import BaseTooltip from "../../components/ui/BaseTooltip";

import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../store/toast/toastSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTimeZoneAbbreviation } from "../../utils/getTimeZoneAbbreviation";
import { workOrderLocalDateTime } from "../../utils/workOrderSchedule";

import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import {
  fetchAllSubCategories,
  fetchAllVendors,
  fetchCategories,
} from "../../services/api/category";
import {
  COUNTER_OFFER_SUB_STATUSES,
  CurrencySymbol,
  PaymentType,
  UserType,
  WorkOrderStatusType,
} from "../../utils/enum";
import { fetchCompanyUsers } from "../../services/api/company";
import useAuth from "../../hooks/useAuth";
import { fetchAllClients } from "../../services/api/client";
import { fetchAllProjects } from "../../services/api/project";
import CustomerWorkOrderFilters from "./CustomerWorkOrderFilters";
import { setClientFilters } from "../../store/filters/clientFiltersSlice";
import {
  deleteWorkOrder,
  fetchUserWorkOrders,
  fetchWorkOrderStatusCounts,
} from "../../services/api/workOrder";
import BaseButton from "../../components/ui/BaseButton";

import GenerateInvoiceModal from "../Invoices/components/GenerateInvoiceModal";
import { fetchTeams } from "../../services/api/teams";
import { useGridApiRef } from "@mui/x-data-grid";
import TableColumnSettings from "../../components/ui/TableColumnSettings";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import UserProfileLink from "../../components/ui/UserProfileLink";

dayjs.extend(utc);
dayjs.extend(timezone);

const LOCKED_COLUMN_FIELDS = ["id"];

const menuOptions = [
  // { label: "Edit", value: "EDIT" },
  // { label: "Delete", value: "DELETE" },
];
const CustomerWorkOrderTable = ({
  setSelectedWorkOrders,
  selectedWorkOrders,
  setIsGenerateInvoiceModalOpen,
  isGenerateInvoiceModalOpen,
  setSelectedClient,
  selectedClient,
  setSelectedStatus,
  setSelectedCurrency,
  selectedCurrency,
}) => {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const companyId = auth?.user?.company?.id;
  // "Customize Columns" settings live in local storage; the hidden ones are
  // sent with the request so the backend leaves their data out.
  const columnStorageKey = `customerWorkOrderListColumnSettings:${auth?.user?.id || "guest"}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const clientFilters = useSelector((s) => s.clientFilters.customerWorkOrders);
  const { page, pageSize } = clientFilters.pagination;

  const defaultFilterParam = searchParams.get("filter") || "";
  const defaultFilterArray = defaultFilterParam.split(",").filter(Boolean);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [filters, setFilters] = useState(() => {
    if (defaultFilterArray.includes("APPROVED")) {
      return { status: ["APPROVED", "CANCELLED_PAYABLE"] };
    } else if (defaultFilterArray.length > 0) {
      return { status: defaultFilterArray };
    } else {
      return { status: ["APPROVED", "CANCELLED_PAYABLE"] };
    }
  });

  const [workOrders, setWorkOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track open dropdown by work order ID
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const [pinnedColumns, setPinnedColumns] = useState({
    left: ["id"], // Pin the ID column to the left
    right: [],
  });
  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });

  const [queryParams, setQueryParams] = useState({
    page: page + 1,
    limit: pageSize,
    status: [...filters.status],
    search: "",
  });
  const filtersPayload = useMemo(
    () => ({
      role: UserType.CLIENT,
      categoryId: clientFilters.categoryId || undefined,
      vendorId: clientFilters.vendorId || undefined,
      subCategoryIds: clientFilters.subCategoryIds || undefined,
      minRate: clientFilters.minRate || undefined,
      maxRate: clientFilters.maxRate || undefined,
      minRequests: clientFilters.minRequests !== "" ? clientFilters.minRequests : undefined,
      maxRequests: clientFilters.maxRequests !== "" ? clientFilters.maxRequests : undefined,
      paymentType: clientFilters.paymentType || undefined,
      startDate: clientFilters.startDate || undefined,
      endDate: clientFilters.endDate || undefined,
      createStartDate: clientFilters.createStartDate || undefined,
      createEndDate: clientFilters.createEndDate || undefined,
      country: clientFilters.country.label || undefined,
      city: clientFilters.city?.label || undefined,
      providerId: clientFilters.provider.value || undefined,
      currency: clientFilters.currency || "",
      engineerCurrency: clientFilters.engineerCurrency || "",
      clientId: clientFilters.clientId || undefined,
      projectId: clientFilters.projectId || undefined,
      teamIds: clientFilters.teamIds || undefined,
      clientIds: clientFilters.clientIds || undefined,
      createdById: clientFilters.createdById || undefined,
      id: clientFilters.id || undefined,
    }),
    [clientFilters],
  );

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <p
          // onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className={`table-font-size font-normal text-primary cursor-pointer`}
          onClick={() => handleNavigate(params.value)}
        >
          {params.row?.referenceId}
        </p>
      ),
    },
    {
      field: "incidentId",
      headerName: "Incident ID",
      width: 150,

      renderCell: (params) => (
        <p
          className={`table-font-size truncate max-w-40 overflow-hidden whitespace-nowrap cursor-default`}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "client",
      width: 200,
      headerName: "Client",
      renderCell: (params) => (
        <p
          className={`cursor-pointer `}
          // onClick={() =>
          //   navigate(`/workOrders/clients/edit/${params?.row?.client?.id}`)
          // }
        >
          {params?.row?.client && params?.row?.client?.clientName}
        </p>
      ),
    },
    {
      field: "project",
      width: 200,
      headerName: "Project",
      renderCell: (params) => (
        <p
          className={`cursor-pointer `}
          // onClick={() =>
          //   navigate(`/workOrders/projects/edit/${params?.row?.project?.id}`)
          // }
        >
          {params?.row?.project && params?.row?.project?.title}
        </p>
      ),
    },
    {
      field: "customerStatus",
      headerName: "Status",
      // Room for "Submitted -> Counter Received" without clipping the sub-status chip.
      width: 260,
      renderCell: (params) => {
        return (
          <>
            <WorkOrderStatus status={params.value} className="!py-[1.8px] !w-[70px] !text-[11px]" />

            {params.value !== "CANCELLED" && params.row?.subStatus && (
              <>
                <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>

                <div className="ml-1">
                  <WorkOrderStatus
                    status={
                      // PO pending and the counter-offer states live on subStatus
                      // for both sides; everything else the customer sees is theirs.
                      params.row.subStatus === "PO_PENDING" ||
                      COUNTER_OFFER_SUB_STATUSES.includes(params.row.subStatus)
                        ? params.row?.subStatus
                        : params.row?.customerSubStatus
                    }
                    className="!py-[1.5px] !min-w-[80px] !w-auto !px-2 whitespace-nowrap !text-[9px]"
                  />
                </div>
              </>
            )}
          </>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      width: 300,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary cursor-pointer"
          onClick={() => handleNavigate(params.row?.id)}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "engineer",
      width: 240,
      headerName: "Engineer",
      renderCell: (params) => {
        const engineer = params?.value;
        if (!engineer) return null;
        const phoneNumber = formatPhoneNumber(engineer.phoneNumber);
        return (
          <div className="flex flex-col justify-center leading-tight whitespace-nowrap font-light">
            <span className="flex items-center gap-1">
              {/* direct=true or the api redirects a vendor's tech to their
                  company owner, opening the vendor's profile instead. */}
              <UserProfileLink
                as="span"
                userId={engineer?.id}
                search="?direct=true"
                className="text-primary cursor-pointer"
              >
                {engineer.displayName || `${engineer.firstName} ${engineer?.lastName ?? ""}`}
              </UserProfileLink>
              {(engineer?.displayId || engineer?.id) && (
                <span className="text-xs text-gray-500">
                  ID: {engineer?.displayId || engineer.id}
                </span>
              )}
            </span>
            {phoneNumber && (
              <span className="text-xs text-gray-500">{formatPhoneNumber(phoneNumber)}</span>
            )}
          </div>
        );
      },
    },
    {
      field: "locationName",
      width: 200,
      headerName: "Location Name",

      renderCell: (params) => (
        <p className={`table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap`}>
          {params?.row?.address?.displayName}
        </p>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 200,
      renderCell: (params) => (
        <div className="cursor-default">
          <BaseTooltip
            position="top"
            interactive={true}
            content={
              <div className="relative pb-2">
                <p className="text-white">{`${params?.value?.addressLine1}, ${params?.value?.country}`}</p>
                {/* <i
                  className="fa-solid fa-diamond-turn-right text-secondary text-xl top-9 right-0 absolute cursor-pointer"
                  onClick={() =>
                    openInGoogleMaps(
                      params?.value?.latitude,
                      params?.value?.longitude
                    )
                  }
                ></i> */}
              </div>
            }
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {`${params?.value?.addressLine1}, ${params?.value?.country}`}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      field: "notes",
      width: 200,
      headerName: "Notes",
      sortable: false,
      renderCell: (params) => (
        <div className="cursor-default">
          <BaseTooltip
            position="top"
            interactive={true}
            content={<p className="text-white">{params.value}</p>}
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {params.value}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      field: "scheduleType",
      headerName: "Schedule",
      width: 200,
      renderCell: (params) => (
        <div>
          <p className={`table-font-size`}>
            {dayjs(params?.row?.orderStartDate)
              // .utc(params?.row?.orderStartDate)
              // .tz(params?.row?.timeZone || "UTC")
              .format("MM/DD/YYYY")}

            <span> at </span>

            {dayjs.utc(params?.row?.orderStartTime).format("hh:mm A")}
            <span> ({getTimeZoneAbbreviation(params?.row?.timeZone) || ""})</span>
          </p>
        </div>
      ),
    },
    {
      field: "localTime",
      headerName: "Local Time",
      width: 200,
      renderCell: (params) => {
        const userTimeZone = dayjs.tz.guess();

        // Null-safe: a draft may have no schedule yet, and dayjs.tz() throws on
        // the "Invalid Date" string rather than returning an invalid dayjs.
        const convertedDateTime = workOrderLocalDateTime(
          params?.row?.orderStartDate,
          params?.row?.orderStartTime,
          params?.row?.timeZone,
        );
        return (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            <p className="table-font-size">
              {convertedDateTime ? (
                <>
                  {convertedDateTime.format("MM/DD/YYYY")}
                  <span> at </span>
                  {convertedDateTime.format("hh:mm A")}
                  <span> ({getTimeZoneAbbreviation(userTimeZone)})</span>
                </>
              ) : (
                "-"
              )}
            </p>
          </div>
        );
      },
    },
    {
      field: "timeLogs",
      width: 200,
      headerName: "Check In",

      renderCell: (params) => (
        <p className={``}>
          {params?.value[0]?.checkInTime && dayjs(params?.value[0]?.checkInTime).format("hh:mm A")}
        </p>
      ),
    },
    {
      field: "timeLogs1",
      width: 200,
      headerName: "Check Out",
      renderCell: (params) => (
        <p className={``}>
          {params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime &&
            dayjs(params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime).format(
              "hh:mm A",
            )}
        </p>
      ),
    },
    {
      field: "hoursWorked",
      width: 200,
      headerName: "Hours Logged",
      renderCell: (params) => (
        // <p className={``}>{params?.value?.toFixed(2) || 0.0} </p>
        <p className={``}>
          {params?.value !== undefined && params?.value !== null && params?.value !== 0
            ? params.value.toFixed(2)
            : ""}
        </p>
      ),
    },
    {
      field: "paymentType",
      headerName: "Customer Rate",
      width: 250,
      renderCell: (params) => {
        return (
          <div
            className="cursor-pointer"
            // onClick={() => navigate(`/workOrder/payment/${params.row?.id}`)}
          >
            {params.row?.paymentType === "FTH" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FTH}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.fthRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "CUSTOM_HOURS" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.CUSTOM_HOURS}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.customHoursRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "HOURLY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HOURLY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.hourlyRate?.toFixed(2)} (min {params.row.minimumHours} hrs)
              </p>
            )}
            {params.row?.paymentType === "HALF_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HALF_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.halfDayRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FULL_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FULL_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params?.row?.fullDayRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FIXED" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FIXED}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || "$"}
                {params?.row?.fixedAmount?.toFixed(2)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      field: "engineerRates",
      headerName: "Engineer Rate",
      width: 320,
      renderCell: (params) => {
        return (
          <>
            {
              <div className="cursor-pointer">
                {params.row?.engineerPaymentType === "FTH" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FTH}</span>

                    {getEngineerValue(params, "fthRate") != null && (
                      <>
                        {" "}
                        @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "fthRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}

                {params.row?.engineerPaymentType === "HOURLY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.HOURLY}</span>

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "minimumHours") != null && (
                      <> (min {getEngineerValue(params, "minimumHours")} hrs)</>
                    )}
                  </p>
                )}

                {params.row?.engineerPaymentType === "HALF_DAY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.HALF_DAY}</span>
                    {getEngineerValue(params, "halfDayRate") != null && (
                      <>
                        {" "}
                        @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "halfDayRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
                {params.row?.engineerPaymentType === "FULL_DAY" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FULL_DAY}</span>
                    {getEngineerValue(params, "fullDayRate") != null && (
                      <>
                        {" "}
                        @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "fullDayRate").toFixed(2)}
                      </>
                    )}

                    {getEngineerValue(params, "hourlyRate") != null && (
                      <>
                        {" "}
                        & Additional hrs @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "hourlyRate").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
                {params.row?.engineerPaymentType === "FIXED" && (
                  <p className="table-font-size">
                    <span className="text-primary">{PaymentType.FIXED}</span>
                    {getEngineerValue(params, "fixedAmount") != null && (
                      <>
                        {" "}
                        @ {CurrencySymbol[params.row?.engineerCurrency] || "$"}
                        {getEngineerValue(params, "fixedAmount").toFixed(2)}
                      </>
                    )}
                  </p>
                )}
              </div>
            }
          </>
        );
      },
    },
    {
      field: "createdBy",
      width: 200,
      headerName: "Created By",
      renderCell: (params) => (
        <UserProfileLink userId={params?.value?.id}>
          {`${params?.value?.firstName} ${params?.value?.lastName}`}
        </UserProfileLink>
      ),
    },
    {
      field: "requested",
      headerName: "Requested",
      width: 100,
      renderCell: (params) => (
        <p
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.requested || 0}
        </p>
      ),
    },
    {
      field: "countered",
      headerName: "Countered",
      width: 100,
      renderCell: (params) => (
        <p
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.countered || 0}
        </p>
      ),
    },
    {
      field: "routed",
      headerName: "Routed",
      width: 100,
      renderCell: (params) => (
        <p
          className={`table-font-size text-primary cursor-pointer`}
          onClick={() => navigate(`/workOrder/providers/${params.row.id}?tab=pending`)}
        >
          {params?.row?.routed || 0}
        </p>
      ),
    },
    {
      field: "purchaseOrders",
      headerName: "Purchase Order",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const { purchaseOrders, purchaseOrderNumber } = params.row;

        if (purchaseOrderNumber && (!purchaseOrders || purchaseOrders.length === 0)) {
          return <p className="cursor-default">{purchaseOrderNumber}</p>;
        }

        if (purchaseOrders?.length) {
          return (
            <div
              className="truncate w-full text-ellipsis whitespace-nowrap"
              title={purchaseOrders.map((po) => po.number).join(", ")}
            >
              <p className="truncate w-full text-ellipsis whitespace-nowrap">
                {purchaseOrders.map((po, index) => (
                  <span key={po.id}>
                    {po.attachment?.url ? (
                      <a
                        href={po.attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary cursor-pointer hover:underline"
                      >
                        {po.number}
                      </a>
                    ) : (
                      <span className="cursor-default">{po.number}</span>
                    )}
                    {index !== purchaseOrders.length - 1 && ", "}
                  </span>
                ))}
              </p>
            </div>
          );
        }

        return <span>-</span>;
      },
    },

    {
      field: "_count",
      width: 200,
      headerName: "Messages",
      renderCell: (params) => (
        <p
          className={`cursor-pointer`}
          onClick={() => navigate(`/workOrder/messages/${params.row.id}`)}
        >
          {params?.row?.messageCount || 0} Msgs
        </p>
      ),
    },
    {
      field: "workOrderManager",
      headerName: "Manager",
      width: 200,
      renderCell: (params) =>
        params?.value && (
          <UserProfileLink userId={params?.value?.id}>
            {`${params?.value?.firstName} ${params?.value?.lastName}`}
          </UserProfileLink>
        ),
    },
    {
      field: "category",
      width: 200,
      headerName: "Type of Work",
      renderCell: (params) => (
        <p className="table-font-size">{`${params?.value?.category || "not specified"}`}</p>
      ),
    },
    {
      field: "createdAt",
      width: 200,
      headerName: "Created",
      renderCell: (params) => (
        <p className={``}>
          {dayjs(params?.value).format("MM/DD/YYYY")} at {dayjs(params?.value).format("hh:mm A")}
        </p>
      ),
    },
    /* {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>
          }
          // onItemClick={(value) => handleItemClick(value, params?.row)}
        />
      ),
      pinRight: true,
    }, */
  ];

  const columnSettings = useTableColumnSettings({
    columns,
    storageKey: columnStorageKey,
    lockedFields: LOCKED_COLUMN_FIELDS,
  });

  // Only the visible columns' data is fetched. "In Flight" is filtered in the
  // browser off orderStartDate, so the schedule column is requested while that
  // filter is on even when it is hidden.
  const visibleColumns = useMemo(() => {
    const fields = columnSettings.visibleFields;
    const needsSchedule = filters.status?.includes("INFLIGHT") && !fields.includes("scheduleType");
    return (needsSchedule ? [...fields, "scheduleType"] : fields).join(",");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSettings.visibleFields, filters.status]);

  const {
    isLoading: isUserWorkOrderLoading,
    error: isWorkOrderError,
    refetch: refetchUserWorkOrders,
    data: workOrdersData,
  } = useQuery({
    queryKey: ["userWorkOrders", queryParams, filtersPayload, visibleColumns],
    queryFn: () =>
      fetchUserWorkOrders({
        ...queryParams,
        ...filtersPayload,
        columns: visibleColumns,
      }),
  });

  const { data: statusCountData, refetch: refetchStatusCounts } = useQuery({
    queryKey: ["workOrderStatusCounts", filtersPayload],
    queryFn: () => fetchWorkOrderStatusCounts(filtersPayload),
  });

  const {
    isLoading: isLoginLoading,
    status,
    error,
    mutateAsync: deleteWorkOrderMutation,
  } = useMutation({
    mutationFn: deleteWorkOrder,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    enabled: isFilterDrawerOpen,
  });

  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: fetchAllVendors,
    enabled: isFilterDrawerOpen,
  });

  const { data: subCategoriesData } = useQuery({
    queryKey: ["subCategories"],
    queryFn: fetchAllSubCategories,
    enabled: isFilterDrawerOpen,
  });

  const companyParams = { id: companyId, filter: true };

  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    error: isCompanyUsersApiError,
  } = useQuery({
    queryKey: ["companyUsers", companyParams.id, companyParams.filter],
    queryFn: () => fetchCompanyUsers(companyParams),
    enabled: !!companyParams.id && isFilterDrawerOpen,
  });

  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", companyId, "entities"],
    queryFn: () =>
      fetchAllClients({
        companyId: companyId?.toString(),
        isParentClient: "false",
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isProjectsLoading, data: projectsData } = useQuery({
    queryKey: ["projects", companyId],
    queryFn: () =>
      fetchAllProjects({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isTeamsLoading, data: teamsData } = useQuery({
    queryKey: ["teams", companyId],
    queryFn: () =>
      fetchTeams({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const categories = categoriesData?.data || [];
  const vendors = vendorsData?.data || [];
  const subCategories = subCategoriesData?.data || [];
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (clientFilters?.country?.value) {
      filters.push({
        prefix: "Country",
        value: clientFilters.country.label,
        key: "country",
      });
    }
    if (clientFilters?.city?.value) {
      filters.push({ prefix: "City", value: clientFilters.city.label, key: "city" });
    }

    if (clientFilters?.provider?.value) {
      filters.push({
        prefix: "Provider",
        value: clientFilters.provider.label,
        key: "provider",
      });
    }
    if (clientFilters?.clientIds?.length > 0) {
      const items = clientsData?.data?.data
        .filter((s) => clientFilters.clientIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.clientName }));

      items?.forEach(({ id, name }) =>
        filters.push({
          prefix: "Client",
          value: name,
          key: "clientIds",
          id,
        }),
      );
    }
    if (clientFilters?.teamIds?.length > 0) {
      const items = teamsData?.data?.data
        .filter((s) => clientFilters.teamIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.name }));

      items?.forEach(({ id, name }) =>
        filters.push({
          prefix: "Team",
          value: name,
          key: "teamIds",
          id,
        }),
      );
    }

    if (clientFilters?.projectId) {
      const projectName = projectsData?.data?.projects?.find(
        (c) => c.id === clientFilters.projectId,
      )?.title;
      if (projectName)
        filters.push({
          prefix: "Project",
          value: projectName,
          key: "projectId",
        });
    }
    if (clientFilters?.currency) {
      const currency = clientFilters.currency || "Currency";
      filters.push({
        prefix: "Currency",
        value: currency,
        key: "currency",
      });
    }
    if (clientFilters?.engineerCurrency) {
      const engineerCurrency = clientFilters.engineerCurrency || "EngineerCurrency";
      filters.push({
        prefix: "Engineer Currency",
        value: engineerCurrency,
        key: "engineerCurrency",
      });
    }

    if (clientFilters?.createdById) {
      const name =
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.firstName +
        " " +
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.lastName;
      if (name) filters.push({ prefix: "Created By", value: name, key: "createdById" });
    }

    if (clientFilters?.id) {
      const id = clientFilters.id || "ID";
      filters.push({
        prefix: "ID",
        value: id,
        key: "id",
      });
    }

    if (clientFilters?.categoryId) {
      const name = categories.find((c) => c.id === clientFilters.categoryId)?.category;
      if (name) filters.push({ prefix: "Technology Stack", value: name, key: "categoryId" });
    }

    if (clientFilters?.vendorId) {
      const name = vendors.find((v) => v.id === clientFilters.vendorId)?.name;
      if (name) filters.push({ prefix: "Category", value: name, key: "vendorId" });
    }

    if (clientFilters?.subCategoryIds?.length > 0) {
      const items = subCategories
        .filter((s) => clientFilters.subCategoryIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.subCategory }));

      items?.forEach(({ id, name }) =>
        filters.push({
          prefix: "Skill",
          value: name,
          key: "subCategoryIds",
          id,
        }),
      );
    }

    if (clientFilters?.minRate || clientFilters?.maxRate) {
      const min = Number(clientFilters.minRate ?? 0);
      const max = Number(clientFilters.maxRate ?? 100);
      filters.push({
        prefix: "Rate",
        value: min === max ? `$${min}` : `$${min} - $${max}`,
        key: "rateRange",
      });
    }

    if (clientFilters?.startDate || clientFilters?.endDate) {
      filters.push({
        prefix: "Date",
        value: `${clientFilters.startDate || "Any"} - ${clientFilters.endDate || "Any"}`,
        key: "dateRange",
      });
    }

    if (clientFilters?.createStartDate || clientFilters?.createEndDate) {
      filters.push({
        prefix: "Date",
        value: `${clientFilters.createStartDate || "Any"} - ${clientFilters.createEndDate || "Any"}`,
        key: "createDateRange",
      });
    }

    if (clientFilters?.paymentType) {
      const label = PaymentType[clientFilters.paymentType] || clientFilters.paymentType;
      filters.push({
        prefix: "Payment",
        value: label,
        key: "paymentType",
      });
    }

    if (clientFilters?.minRequests !== "" || clientFilters?.maxRequests !== "") {
      const min = clientFilters.minRequests ?? 0;
      const max = clientFilters.maxRequests ?? 0;

      filters.push({
        prefix: "Requests",
        value: min === max ? `${min}` : `${min} - ${max}`,
        key: "requestsRange",
      });
    }

    return filters;
  }, [clientFilters, categories, vendors, subCategories]);
  const hasAppliedFilters = appliedFilters.length > 0;

  const statusCounts = statusCountData?.statusCounts || {};
  const totalWorkOrdersCount = statusCountData?.totalCount || 0;
  const rowCount = search
    ? workOrdersData?.data?.totalCount || 0
    : filters.status?.[0]
      ? filters.status.reduce((sum, s) => sum + (statusCounts[s] || 0), 0)
      : totalWorkOrdersCount;

  useEffect(() => {
    if (!clientFilters || !appliedFilters) return;

    if (filters) {
      setSelectedStatus(filters?.status);
    }

    const selectedClientId = clientFilters?.clientIds?.length === 1 && clientFilters?.clientIds[0];

    const selectedClientName = appliedFilters.find((filter) => filter.key === "clientIds")?.value;

    const fullClient = clientsData?.data?.data?.find((c) => c.id === selectedClientId);

    setSelectedCurrency(clientFilters?.currency);

    setSelectedClient((prev) => {
      if (
        prev?.clientId === selectedClientId &&
        prev?.clientName === selectedClientName &&
        prev?.branchId === fullClient?.branchId
      ) {
        return prev;
      }

      return {
        clientId: selectedClientId,
        clientName: selectedClientName,
        branchId: fullClient?.branchId || null,
        branchName: fullClient?.branch?.name || null,
      };
    });
  }, [appliedFilters, clientFilters, clientsData]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: page + 1,
      status: [...filters.status],
      search: "",
    }));
    setSearch("");

    applyFilter();
  }, [filters]);

  useEffect(() => {
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [filtersChanged]);

  useEffect(() => {
    setPaginationModel({ page, pageSize });
    setQueryParams((prev) => ({ ...prev, page: page + 1, limit: pageSize }));
  }, [page, pageSize]);

  const applyFilter = () => {
    const allWorkOrders = workOrdersData?.data?.workOrders || [];

    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(allWorkOrders);
      return;
    }

    if (filters.status.includes("INFLIGHT")) {
      const filtered = filterInFlightWorkOrders();
      setWorkOrders(filtered);
      return;
    }

    // The server already filtered by status; only re-filter when the Status
    // column's data was actually requested.
    const filteredWorkOrders = allWorkOrders.filter(
      (wo) => !("customerStatus" in wo) || filters.status.includes(wo.customerStatus),
    );

    setWorkOrders(filteredWorkOrders);
  };

  const normalizeStatus = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === "string") return [input];
    return [""];
  };

  const handleFilterClick = (status) => {
    const statusArray = normalizeStatus(status);
    setFilters({ status: statusArray });

    const queryString = statusArray.filter(Boolean).join(",");
    setSearchParams({ filter: queryString });
  };

  if (isWorkOrderError?.response?.status === 401) {
    clearAuthStorage();
    navigateWithReload("/getStarted");
  }

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedWorkOrder(item);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedWorkOrder(null);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
    dispatch(
      setClientFilters({
        module: "customerWorkOrders",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  const convertToLocalTime = (time, timeZone) => {
    if (!timeZone) return;

    const targetTime = dayjs(time).tz(timeZone, true);

    const localTime = targetTime.local().format("hh:mm A");

    return localTime;
  };

  const getEngineerValue = (params, field) => {
    const offer = params.row?.engineer?.offer;

    const offerFieldMap = {
      hourlyRate: offer?.hourlyRate,
      fullDayRate: offer?.fullDayRate,
      halfDayRate: offer?.halfDayRate,
      fthRate: offer?.fthRate,
      fixedAmount: offer?.fixedAmount,
      minimumHours: offer?.minimumHours,
    };

    const rowFieldMap = {
      hourlyRate: params.row?.engineerHourlyRate,
      fullDayRate: params.row?.engineerFullDayRate,
      halfDayRate: params.row?.engineerHalfDayRate,
      fthRate: params.row?.engineerFthRate,
      fixedAmount: params.row?.engineerFixedAmount,
      minimumHours: params.row?.engineerMinimumHours,
    };

    return offerFieldMap[field] ?? rowFieldMap[field] ?? null;
  };

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(`/workOrder/edit/summary/${params?.id}`);
    }
    if (value === "DELETE") {
      handleDeleteWorkOrder(params?.id);
    }
  };

  const handleNavigate = (id) => {
    navigate(`/workOrder/details/${id}?fromCustomerBoard=true`);
  };

  //this will initially run to set the work orders data fetched from API
  useEffect(() => {
    setWorkOrders(workOrdersData?.data.workOrders);
    applyFilter();
  }, [workOrdersData]);

  //this is filter the work order based on current filter selected without API call
  useEffect(() => {
    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(workOrdersData?.data.workOrders);
    } else if (filters.status.includes("INFLIGHT")) {
      const filteredWorkOrders = filterInFlightWorkOrders();
      setWorkOrders(filteredWorkOrders);
    } else {
      const filteredWorkOrders = workOrdersData?.data.workOrders.filter(
        (workOrder) =>
          !("customerStatus" in workOrder) || filters.status.includes(workOrder.customerStatus),
      );
      setWorkOrders(filteredWorkOrders);
    }
  }, [filters]);

  const filterInFlightWorkOrders = () => {
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    const filteredWorkOrders = workOrdersData?.data.workOrders.filter((workOrder) => {
      const workOrderDate = dayjs(workOrder.orderStartDate).startOf("day");
      return workOrderDate.isSame(today, "day") || workOrderDate.isSame(tomorrow, "day");
    });

    return filteredWorkOrders;
  };

  const toggleDropdown = (id) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null); // Close the dropdown if it's already open
    } else {
      setOpenDropdownId(id); // Open the dropdown for the selected row
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      const payload = {
        id: workOrderId,
      };
      const response = await deleteWorkOrderMutation(payload);
      setOpenDropdownId(null); // Close the dropdown if it's already open
      dispatch(
        showToast({
          message: "Work Order deleted successfully",
          type: "success",
        }),
      );
      refetchUserWorkOrders();
      refetchStatusCounts();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleSearchChange = useCallback(
    debounce((val) => {
      setQueryParams((prev) => ({
        ...prev,
        search: val,
        page: 1,
      }));
    }, 500),
    [],
  );

  const handleRemoveFilter = (key, id) => {
    if (key === "rateRange") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "minRate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "maxRate",
          data: "",
        }),
      );
    } else if (key === "requestsRange") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "minRequests",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "maxRequests",
          data: "",
        }),
      );
    } else if (key === "dateRange") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "startDate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "endDate",
          data: "",
        }),
      );
    } else if (key === "createDateRange") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "createStartDate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "createEndDate",
          data: "",
        }),
      );
    } else if (["provider", "projectId", "createdById", "id"].includes(key)) {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: key,
          data: "",
        }),
      );
    } else if (key === "city") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "country") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "city",
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "clientIds") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "clientIds",
          data: clientFilters.clientIds.filter((clientId) => clientId !== id),
        }),
      );
      const selectedProject = projectsData?.data?.projects?.find(
        (p) => p.id === clientFilters.projectId,
      );

      if (selectedProject?.client?.id === id) {
        dispatch(
          setClientFilters({
            module: "customerWorkOrders",
            filterName: "projectId",
            data: null,
          }),
        );
      }
    } else if (key === "subCategoryIds") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "subCategoryIds",
          data: clientFilters.subCategoryIds.filter((subId) => subId !== id),
        }),
      );
    } else if (key === "vendorId") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "categoryId") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "categoryId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "teamIds") {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: "teamIds",
          data: clientFilters.teamIds.filter((teamId) => teamId !== id),
        }),
      );
    } else {
      dispatch(
        setClientFilters({
          module: "customerWorkOrders",
          filterName: key,
          data: "",
        }),
      );
    }

    dispatch(
      setClientFilters({
        module: "customerWorkOrders",
        filterName: "pagination",
        data: { page: 0, pageSize: clientFilters.pagination.pageSize },
      }),
    );

    setFiltersChanged(true);
  };

  return (
    <>
      {isUserWorkOrderLoading && <BaseLoader isLoading={isUserWorkOrderLoading} />}
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="flex items-center border-r border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>

            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => {
                  return (
                    <div
                      key={(f.key || "") + (f.value || "") + (f.id || "")}
                      className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                    >
                      <span className="text-primary">{f.prefix}:</span>&nbsp;
                      <p className="text-primary text-sm mx-1">{f.value}</p>
                      <button
                        onClick={() => handleRemoveFilter(f.key, f.id)}
                        className="ml-2 text-primary hover:text-red-500"
                        aria-label={`Remove ${f.prefix} ${f.value}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={columnSettings.open}
                className="flex items-center gap-2 h-[36px] px-3 rounded-md border border-primary text-primary text-[13px] font-light hover:bg-[#EAF1FF] whitespace-nowrap"
                title="Customize columns"
              >
                <i className="fa-light fa-table-columns text-sm" />
                <span>Customize View</span>
              </button>
              <BaseTextField
                placeholder="Search by WO title or reference ID"
                onChange={(e) => {
                  setSearch(e.target.value);
                  handleSearchChange(e.target.value);
                }}
                value={search}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg" />}
                className="w-full sm:w-[300px] lg:w-[350px]"
              />
            </div>
          </div>
        </div>

        <div className="my-4 flex overflow-auto">
          <div className="mr-5">
            <FilterChip
              filter="Approved"
              count={
                search && filters.status.includes("APPROVED")
                  ? workOrdersData?.data?.totalCount
                  : (statusCounts?.APPROVED || 0) + (statusCounts?.CANCELLED_PAYABLE || 0)
              }
              active={filters.status.includes("APPROVED") ? true : false}
              onClick={() => handleFilterClick(["APPROVED", "CANCELLED_PAYABLE"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Invoiced"
              count={
                search && filters.status.includes("INVOICED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.INVOICED || 0
              }
              active={filters.status.includes("INVOICED") ? true : false}
              onClick={() => handleFilterClick(["INVOICED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Paid"
              count={
                search && filters.status.includes("PAID")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PAID || 0
              }
              active={filters.status.includes("PAID") ? true : false}
              onClick={() => handleFilterClick(["PAID"])}
            />
          </div>
        </div>

        <BaseDataTableV2
          apiRef={apiRef}
          className="max-h-[calc(100vh-300px)] overflow-auto"
          rowHeight={38}
          columns={columnSettings.orderedColumns}
          columnVisibilityModel={columnSettings.columnVisibilityModel}
          data={workOrdersData?.data?.workOrders || []}
          isLoading={false}
          disableColumnResize={false}
          onRowSelectionModelChange={(workOrderIds) => {
            const selectedRows = workOrderIds?.map((id) => apiRef.current.getRow(id));
            setSelectedWorkOrders(selectedRows);
            console.log("Selected Rows:", selectedRows);
          }}
          checkboxSelection
          hideFooter={false}
          pagination
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel} // Connect paginationModel
          onPaginationModelChange={handlePaginationChange} // Handle page and pageSize change
          paginationMode="server"
          disableVirtualization
          sx={{
            "& .MuiDataGrid-columnHeader[data-field='__check__'], & .MuiDataGrid-cell[data-field='__check__']":
              {
                position: "sticky",
                left: 0,
                zIndex: 2,
              },
            "& .MuiDataGrid-columnHeader[data-field='id'], & .MuiDataGrid-cell[data-field='id']": {
              position: "sticky",
              left: 50, // width of the checkbox column
              zIndex: 2,
              borderRight: "1px solid #e0e0e0",
            },
            // Frozen header cells sit above frozen body cells; header is white.
            "& .MuiDataGrid-columnHeader[data-field='__check__'], & .MuiDataGrid-columnHeader[data-field='id']":
              {
                zIndex: 3,
                backgroundColor: "#fff",
              },
            "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#f6f6f6",
              },
            "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#fff",
              },
            "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#f1f1f1",
              },
            "& .MuiDataGrid-row.header-row .MuiDataGrid-cell[data-field='__check__'], & .MuiDataGrid-row.header-row .MuiDataGrid-cell[data-field='id']":
              {
                backgroundColor: "#EFF4FF",
              },
          }}
        />
        {columnSettings.isOpen && (
          <TableColumnSettings
            open={columnSettings.isOpen}
            onClose={columnSettings.close}
            title="Customize Columns"
            columns={columnSettings.columnSettingsList}
            visibility={columnSettings.columnVisibility}
            lockedFields={LOCKED_COLUMN_FIELDS}
            onApply={columnSettings.applySettings}
            onReset={columnSettings.resetSettings}
          />
        )}

        {isFilterDrawerOpen && (
          <CustomerWorkOrderFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchUserWorkOrders={fetchUserWorkOrders}
            currentStatus={filters.status}
            refetchStatusCounts={refetchStatusCounts}
            isWorkOrderLoading={isUserWorkOrderLoading}
            setFiltersChanged={setFiltersChanged}
            hasAppliedFilters={hasAppliedFilters}
            appliedFilters={appliedFilters}
            projectsData={projectsData}
            clientsData={clientsData}
            teamsData={teamsData}
            companyUsers={companyUsers}
          />
        )}

        {isGenerateInvoiceModalOpen && (
          <GenerateInvoiceModal
            open={isGenerateInvoiceModalOpen}
            close={setIsGenerateInvoiceModalOpen}
            selectedWorkOrderIds={selectedWorkOrders?.map((workOrder) => workOrder?.id)}
            client={selectedClient}
            role={UserType.CLIENT}
            currency={selectedCurrency}
          />
        )}
      </BaseWrapper>
    </>
  );
};

export default CustomerWorkOrderTable;
